import { useCallback, useMemo } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { ColoredImageIcon } from '@room-match/shared-ui-components';
import { FileType } from 'shared/enums/FileType';
import { MediaFileType } from 'shared/enums/MediaFileType';
import { useEditProfileMediaDocumentData } from '../../hooks/useEditProfileMediaDocumentData';
import { ImageThumbnailContainer } from 'pages/Representatives/components/ImageThumbnail';
import { array_skeletons } from 'pages/Representatives/const/array_skeleton';
import { EditProfileCustomSkeleton } from '../../EditProfile.styles';
import { EditProfileDocumentsImageListContainer } from './EditProfileMediaDocumentsImage.styles';
import { EditProfileMediaDocumentsImageItem } from './EditProfileMediaDocumentsImageItem/EditProfileMediaDocumentsImageItem';
import { useEditProfileMediaHideClick } from '../../hooks/useEditProfileMediaHideClick';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';
import { useEditProfileMediaImageEdit } from '../../hooks/useEditProfileMediaImageEdit';
import { EttachedHiddenType } from 'shared/enums/EAttachedHiddenType';
import { IRepresentativeSingleProfileMedia } from 'shared/interfaces/IRepresentativesEditProfile';
import EditProfileMediaDocumentAccordion from '../EditProfileMediaDocumentAccordion/EditProfileMediaDocumentAccordion';
import EditProfileMediaDocumentDivider from '../EditProfileMediaDocumentDivider/EditProfileMediaDocumentDivider';
import RepresentativeErrorUI from 'pages/Representatives/components/RepresentativeErrorUI/RepresentativeErrorUI';
import EditProfileMediaDnD from '../EditProfileMediaDnD/EditProfileMediaDnD';
import { useEditProfileMediaGallery } from '../../hooks/useEditProfileMediaGallery';
import AsyncMediaGallery from 'components/AsyncMediaGallery/AsyncMediaGallery';
import { useTheme } from '@material-ui/core';

const EditProfileMediaDocumentsImage = () => {
  const theme = useTheme();
  const {
    attached_media: attached_images,
    hidden_media,
    completeness,
    media_loading,
    media_error,
    handleRefetch,
    profileId,
  } = useEditProfileMediaDocumentData(FileType.IMAGE);
  const { onAttachedEditorClick, onHiddenEditorClick } = useEditProfileMediaImageEdit();
  const { onHideClick, unHideClick } = useEditProfileMediaHideClick(
    FileType.IMAGE,
    profileId,
    AgencyPermissionEnums.IMAGES,
  );

  const { open, selectedIndex, handleOpen, handleClose, mappedData, attachedItemRef } = useEditProfileMediaGallery({
    hidden_media,
    file_type: MediaFileType.IMAGE,
  });

  // Memoization for the Rendered Group in DND component
  const renderedGroupMemoized = useCallback((attached_data: IRepresentativeSingleProfileMedia[]) => {
    attachedItemRef.current = attached_data;
    return attached_data.map((attached_image, index) => (
      <li style={{ listStyle: 'none' }} key={attached_image.id}>
        <EditProfileMediaDocumentsImageItem
          type={EttachedHiddenType.ATTACHED}
          image={{
            url:
              attached_image?.medium?.modified_attachment_url ||
              attached_image?.medium.cropped_attachment_url ||
              attached_image?.medium?.attachment_url,
            file_name: attached_image?.medium?.file_name,
            primary: attached_image?.primary,
            id: attached_image.id || '',
          }}
          index={index + 1}
          onEditorClick={() => onAttachedEditorClick(attached_image)}
          onHideClick={() => {
            onHideClick(attached_image?.id);
          }}
          onViewClick={() => {
            handleOpen(index);
          }}
        />
      </li>
    ));
  }, []);

  // Memoization for the Overlay in DND component
  const renderedOverlayMemoized = useCallback(
    (activeData: IRepresentativeSingleProfileMedia, activeId: number) => (
      <EditProfileMediaDocumentsImageItem
        image={{
          url:
            activeData.medium?.modified_attachment_url ||
            activeData.medium.cropped_attachment_url ||
            activeData.medium?.attachment_url,
          file_name: activeData.medium?.file_name,
          primary: activeData.primary,
          id: activeData.id || '',
        }}
        index={activeId + 1}
      />
    ),
    [],
  );

  // Memoized Hidden Media
  const hiddenMediaMemoized = useMemo(() => {
    return hidden_media?.map((hidden_image, index) => (
      <li key={hidden_image.id} style={{ listStyle: 'none' }}>
        <EditProfileMediaDocumentsImageItem
          type={EttachedHiddenType.HIDDEN}
          image={{
            url:
              hidden_image?.modified_attachment_url ||
              hidden_image?.cropped_attachment_url ||
              hidden_image?.attachment_url,
            primary: false,
            id: hidden_image.id,
          }}
          onViewClick={() => {
            handleOpen(index, true);
          }}
          onEditorClick={() => {
            onHiddenEditorClick(hidden_image);
          }}
          onHideClick={() => {
            unHideClick({
              id: hidden_image.id,
              tag_list: MediaFileType.IMAGE,
            });
          }}
        />
      </li>
    ));
  }, [hidden_media]);

  return (
    <Fragment>
      {open ? (
        <AsyncMediaGallery
          openGallery={open}
          selectedIndex={selectedIndex}
          handleOpenGallery={handleClose}
          data={mappedData}
          type={'image'}
          sharedTheme={theme.palette.type}
        />
      ) : null}
      <EditProfileMediaDocumentAccordion
        type={FileType.IMAGE}
        usedLength={attached_images?.length || 0}
        maxLength={(hidden_media?.length || 0) + (attached_images?.length || 0)}
        header_icon={<ColoredImageIcon />}
        completeness={completeness}
      >
        {media_loading ? (
          <EditProfileDocumentsImageListContainer>
            {array_skeletons.map((_, index) => (
              <ImageThumbnailContainer key={index}>
                <EditProfileCustomSkeleton width="100%" height="100%" variant="rect" />
              </ImageThumbnailContainer>
            ))}
          </EditProfileDocumentsImageListContainer>
        ) : media_error ? (
          <RepresentativeErrorUI message={'There was a problem getting your Image/s'} onButtonClick={handleRefetch} />
        ) : (
          <Fragment>
            {/* Attached List */}
            <EditProfileDocumentsImageListContainer>
              <EditProfileMediaDnD
                data={attached_images}
                profileId={profileId}
                agencyPermissionValue={AgencyPermissionEnums.IMAGES}
                renderGroup={renderedGroupMemoized}
                renderDragOverlay={renderedOverlayMemoized}
              />
            </EditProfileDocumentsImageListContainer>

            {/* Hidden List */}
            {hidden_media?.length === 0 ? null : (
              <EditProfileMediaDocumentDivider type={MediaFileType.IMAGE} key={'hidden'}>
                <EditProfileDocumentsImageListContainer>{hiddenMediaMemoized}</EditProfileDocumentsImageListContainer>
              </EditProfileMediaDocumentDivider>
            )}
          </Fragment>
        )}
      </EditProfileMediaDocumentAccordion>
    </Fragment>
  );
};

export default EditProfileMediaDocumentsImage;
